import { useEffect, useState } from "react";
import { Loader2, MapPin } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { fetchPublicEvent, type PublicEvent } from "@/api/eventDiscovery";
import { EventDateTime } from "@/components/events/EventDateTime";
import { formatMoney, pwywSymbol } from "@/lib/currency";
import { WhatToExpect } from "./RegistrationDialog/NotRegisteredView";

/**
 * Signed-out registration card. Shows public event info + a "Sign in to
 * register" button that opens the sign-in dialog. Google and email
 * confirmation redirects preserve the current URL (query included), so the
 * visitor returns to this same /events/register?event_id=… ready to register.
 */
export default function GuestRegisterCard({ eventId }: { eventId: number }) {
  const { setAuthModalOpen } = useAuth();
  const [event, setEvent] = useState<PublicEvent | null>(null);
  const [state, setState] = useState<"loading" | "error" | "notfound" | "ok">(
    "loading",
  );

  useEffect(() => {
    let cancelled = false;
    fetchPublicEvent(eventId)
      .then((e) => {
        if (cancelled) return;
        if (!e) return setState("notfound");
        setEvent(e);
        setState("ok");
      })
      .catch(() => !cancelled && setState("error"));
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  if (state === "loading") {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading event…
      </div>
    );
  }
  if (state === "notfound") {
    return (
      <p className="text-sm text-muted-foreground">
        That event could not be found. It may have been removed.
      </p>
    );
  }
  if (state === "error" || !event) {
    return (
      <p className="text-sm text-muted-foreground">
        Couldn't load this event. Please try again.
      </p>
    );
  }

  const where = event.is_virtual
    ? "Virtual"
    : [event.city, event.country].filter(Boolean).join(", ");

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Quizrun #{event.quizrun_number}
          {where ? ` — ${where}` : ""}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1 text-sm text-muted-foreground">
          <EventDateTime
            start={event.date_start}
            tz={event.timezone}
            variant="datetime"
            layout="stacked"
            showIcon
          />
          {where ? (
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              {where}
            </div>
          ) : null}
        </div>

        <WhatToExpect isVirtual={event.is_virtual} />

        {/* Same PWYW range as the signed-in register view. Real prices render
            as the amount; unset = tiered pay-what-you-want. */}
        <p className="text-sm">
          <span className="font-medium">Price:</span>{" "}
          {event.price_cents != null && event.price_cents > 0
            ? formatMoney(event.price_cents, event.price_currency ?? null)
            : `${pwywSymbol(event.price_currency)}5–20+ — pay what you want`}
        </p>

        {/* Guests can't be classified as newcomer vs returning, so state both
            paths neutrally rather than promising a free event to everyone. */}
        <p className="text-xs text-muted-foreground">
          First regular event is free for newcomers. Otherwise no payment now —
          pay on the day.
        </p>

        <Button onClick={() => setAuthModalOpen(true)} className="w-full">
          Sign in to register
        </Button>
      </CardContent>
    </Card>
  );
}
